// Custom hooks for user-related API calls
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { updateUserDetails, registerUser, setAppearance, getWhatsAppStatus, linkWhatsApp, removeWhatsApp } from '../api';
import { useUser } from '../../context/UserContext';

/**
 * Custom hook to update user details using react-query mutation
 * @returns {Object} - { mutate, isPending, isError, error, isSuccess }
 */
export const useUpdateUserDetailsMutation = () => {
  const { email } = useUser();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ name, surname }) => {
      if (!email) {
        throw new Error('User email is required');
      }
      return updateUserDetails(email, name, surname);
    },
    onSuccess: async () => {
      // Invalidate user details query to refetch updated data
      await queryClient.invalidateQueries({ queryKey: ['userDetails', email] });
    },
    onError: (error) => {
      console.error('Failed to update user details:', error);
    },
  });
};

/**
 * Custom hook to add user to database using react-query mutation
 * @returns {Object} - { mutate, isPending, isError, error, isSuccess }
 */
export const useAddUserDbMutation = () => {
  return useMutation({
    mutationFn: ({ email, name, surname }) => {
      if (!email || !name || !surname) {
        throw new Error('Email, name, and surname are required');
      }
      return registerUser(email, null, name, surname);
    },
    onError: (error) => {
      console.error('Failed to add user to database:', error);
    },
  });
};

/**
 * Custom hook to set user appearance/theme preference using react-query mutation
 * @returns {Object} - { mutate, mutateAsync, isPending, isError, error, isSuccess }
 */
export const useSetAppearanceMutation = () => {
  const { email } = useUser();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (appearance) => {
      if (!appearance || !['dark_mode', 'light_mode'].includes(appearance)) {
        throw new Error('Valid appearance value is required (dark_mode or light_mode)');
      }
      return setAppearance(appearance);
    },
    onMutate: async (appearance) => {
      if (!email) return;
      await queryClient.cancelQueries({ queryKey: ['userDetails', email] });
      const previous = queryClient.getQueryData(['userDetails', email]);
      queryClient.setQueryData(['userDetails', email], (old) =>
        old ? { ...old, appearance } : old
      );
      return { previous };
    },
    onError: (error, _appearance, context) => {
      console.error('Failed to set appearance:', error);
      if (email && context?.previous !== undefined) {
        queryClient.setQueryData(['userDetails', email], context.previous);
      }
    },
    onSettled: async () => {
      if (email) {
        await queryClient.invalidateQueries({ queryKey: ['userDetails', email] });
      }
    },
  });
};

/**
 * Custom hook to get WhatsApp linking status
 * @returns {Object} - { data, isLoading, isError, error, refetch }
 */
export const useWhatsAppStatus = () => {
  return useQuery({
    queryKey: ['whatsappStatus'],
    queryFn: getWhatsAppStatus,
    staleTime: 30000, // 30 seconds
  });
};

/**
 * Custom hook to link WhatsApp number
 * @returns {Object} - { mutate, mutateAsync, isPending, isError, error, isSuccess }
 */
export const useLinkWhatsAppMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ countryCode, nationalNumber }) => {
      if (!countryCode || !nationalNumber) {
        throw new Error('Country code and phone number are required');
      }
      return linkWhatsApp(countryCode, nationalNumber);
    },
    onSuccess: (data) => {
      if (data.status) {
        queryClient.invalidateQueries({ queryKey: ['whatsappStatus'] });
      }
    },
    onError: (error) => {
      console.error('Failed to link WhatsApp:', error);
    },
  });
};

/**
 * Custom hook to remove WhatsApp number
 * @returns {Object} - { mutate, mutateAsync, isPending, isError, error, isSuccess }
 */
export const useRemoveWhatsAppMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: removeWhatsApp,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['whatsappStatus'] });
    },
    onError: (error) => {
      console.error('Failed to remove WhatsApp:', error);
    },
  });
};

